import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { apiFetch, apiPatch, apiPost } from "@/lib/api";

const safetyIncidentKeys = {
  all: ["safety-incidents"] as const,
  list: (filters?: Record<string, unknown>) =>
    ["safety-incidents", "list", filters ?? null] as const,
  detail: (id: string) => ["safety-incidents", id] as const,
} as const;

export type InjurySeverity = "none" | "first_aid" | "recordable" | "lost_time" | "fatal";

export interface CorrectiveAction {
  id: string;
  incidentId: string;
  description: string;
  ownerId: string | null;
  ownerName: string | null;
  dueDate: string | null;
  completedAt: string | null;
  completedById: string | null;
  completedByName: string | null;
  closureNote: string | null;
}

export interface SafetyIncident {
  id: string;
  projectId: string;
  projectName: string | null;
  incidentNumber: string;
  orgId: string;
  incidentDate: string;
  incidentTime: string | null;
  location: string;
  injurySeverity: InjurySeverity;
  description: string;
  personsInvolved: string | null;
  contributingFactors: string | null;
  witnessStatements: string | null;
  isLocked: boolean;
  createdAt: string;
  createdByUserId: string;
  createdByName: string | null;
  status: "open" | "closed";
  correctiveActions: CorrectiveAction[];
}

export interface SafetyIncidentFilters {
  projectId?: string;
  severity?: string;
  dateFrom?: string;
  dateTo?: string;
}

function buildSafetyIncidentUrl(filters?: SafetyIncidentFilters): string {
  const params = new URLSearchParams();
  if (filters?.projectId) params.set("projectId", filters.projectId);
  if (filters?.severity) params.set("severity", filters.severity);
  if (filters?.dateFrom) params.set("dateFrom", filters.dateFrom);
  if (filters?.dateTo) params.set("dateTo", filters.dateTo);
  const qs = params.toString();
  return qs ? `/safety-incidents?${qs}` : "/safety-incidents";
}

export function useSafetyIncidents(filters?: SafetyIncidentFilters) {
  return useQuery({
    queryKey: safetyIncidentKeys.list(filters as Record<string, unknown> | undefined),
    queryFn: () => apiFetch<SafetyIncident[]>(buildSafetyIncidentUrl(filters)),
  });
}

export function useSafetyIncident(id: string, enabled = true) {
  return useQuery({
    queryKey: safetyIncidentKeys.detail(id),
    queryFn: () => apiFetch<SafetyIncident>(`/safety-incidents/${id}`),
    enabled,
  });
}

export interface CreateSafetyIncidentInput {
  projectId: string;
  incidentDate: string;
  incidentTime?: string;
  location: string;
  injurySeverity: InjurySeverity;
  description: string;
  personsInvolved?: string;
  contributingFactors?: string;
  witnessStatements?: string;
}

export function useCreateSafetyIncident() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateSafetyIncidentInput) =>
      apiPost<SafetyIncident>("/safety-incidents", input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: safetyIncidentKeys.all });
      toast.success("Incident reported");
    },
    onError: (err: Error) => {
      toast.error(err.message ?? "Failed to report incident");
    },
  });
}

export interface AddCorrectiveActionInput {
  incidentId: string;
  description: string;
  ownerId?: string;
  dueDate?: string;
}

export function useAddCorrectiveAction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ incidentId, ...body }: AddCorrectiveActionInput) =>
      apiPost<CorrectiveAction>(`/safety-incidents/${incidentId}/corrective-actions`, body),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: safetyIncidentKeys.all });
      void qc.invalidateQueries({ queryKey: safetyIncidentKeys.detail(vars.incidentId) });
      toast.success("Corrective action added");
    },
    onError: (err: Error) => {
      toast.error(err.message ?? "Failed to add corrective action");
    },
  });
}

export interface CloseCorrectiveActionInput {
  incidentId: string;
  actionId: string;
  closureNote: string;
}

export function useCloseCorrectiveAction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ incidentId, actionId, ...body }: CloseCorrectiveActionInput) =>
      apiPatch<CorrectiveAction>(
        `/safety-incidents/${incidentId}/corrective-actions/${actionId}/close`,
        body,
      ),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: safetyIncidentKeys.all });
      void qc.invalidateQueries({ queryKey: safetyIncidentKeys.detail(vars.incidentId) });
      toast.success("Corrective action closed");
    },
    onError: (err: Error) => {
      toast.error(err.message ?? "Failed to close corrective action");
    },
  });
}
